import axios, { AxiosResponse } from 'axios';
import {
  CustomizationComment,
  InstancesResponse,
  InstanceMetadataResponse,
  ClusterSimpleSearch,
  SubClusterUpdateData,
  SelfManagedClusterUpdateData,
  SubClusterCreateData,
  SelfManagedClusterCreateData,
  SizesResult,
  ClusterScaleBody,
  ServicesStatusResponse,
  MaxIpuInfo,
  ClusterDetailsData,
  SparkImagesSizesData,
  ClusterServiceConsumption,
} from 'types/cluster';
import { API_URL } from 'utils/http';
import { UpgradeHistoryResponse } from 'types/upgradeHistory';

export function getClusterByName(name: string) {
  return axios.get(`${API_URL}/instances/${name}`);
}

export function addCustomizationComment(
  name: string,
  comment: CustomizationComment,
) {
  return axios.post<{ comments: CustomizationComment[] }>(
    `${API_URL}/instances/${name}/customization-comments`,
    comment,
  );
}

export function deleteCustomizationComments(name: string, dates: string[]) {
  return axios.delete<{ comments: CustomizationComment[] }>(
    `${API_URL}/instances/${name}/customization-comments`,
    { data: { dates } },
  );
}

export function dismissCustomizationComments(name: string, dates: string[]) {
  return axios.patch<{ comments: CustomizationComment[] }>(
    `${API_URL}/instances/${name}/customization-comments/dismiss`,
    { dates },
  );
}

export function getClusters({
  page = 1,
  search,
  userid = null,
  limit = 10,
  from,
}: {
  page?: number;
  search?: string;
  userid?: string | null;
  limit?: number;
  from?: string;
}) {
  const params: Record<string, any> = { page, search, userid, limit };
  if (from) params.from = from;
  return axios.get<InstancesResponse>(`${API_URL}/instances`, { params });
}

export function simpleClusterSearchWithAttribute<T>({
  attribute,
  search,
}: {
  attribute: string;
  search: string;
}) {
  return axios.get<ClusterSimpleSearch<T>>(
    `${API_URL}/instances/names-and-ids`,
    {
      params: { attributes: attribute, search },
    },
  );
}

export function getClusterTenants({ userId, clusterName }) {
  return axios.get(`${API_URL}/tenants/${userId}/instances/${clusterName}`);
}

export function updateCluster(name, cluster) {
  return axios.put(`${API_URL}/instances/${name}/update`, cluster);
}

export function getInstanceSchema() {
  return axios.get<InstanceMetadataResponse[]>(`${API_URL}/instances/schema`);
}

export function deleteCluster(name) {
  return axios.delete(`${API_URL}/instances/${name}/delete`);
}

export function softDeleteCluster(name) {
  return axios.post(`${API_URL}/instances/${name}/soft-delete`);
}

export function restoreSoftDeleteCluster(name) {
  return axios.post(`${API_URL}/instances/${name}/restore-from-soft-delete`);
}

export function extendSoftDeleteCluster(name, extensionDays) {
  return axios.post(`${API_URL}/instances/${name}/extend-soft-delete`, {
    extensionDays,
  });
}

export function deleteMultiCluster(clusterIds) {
  return axios.post(`${API_URL}/instances/delete`, { clusterIds });
}

export function ldapSync(ldapRecord) {
  return axios.post(
    `${API_URL}/instances/${ldapRecord.instanceName}/ldapSync`,
    {
      ldapRecord: ldapRecord,
    },
  );
}

export function scheduler(schedulerRecord) {
  return axios.post(
    `${API_URL}/instances/${schedulerRecord.instanceName}/scheduler`,
    {
      schedulerRecord: schedulerRecord,
    },
  );
}

export function blockClusterFromUrl(name, blockage) {
  return axios.put(`${API_URL}/instances/${name}/blockformurl`, {
    blockage: blockage,
  });
}

export function oneClickMLMultiCluster(oneClickML, clusterIds) {
  const params = {
    oneClickML,
    clusterIds,
  };
  return axios.post(`${API_URL}/instances/oneclickml`, params);
}

export function tenantConfigCluster(key, value, clusterIds) {
  const params = {
    key,
    value,
    clusterIds,
  };
  return axios.post(`${API_URL}/instances/tenantConfig`, params);
}

export function blockMultiClusterFromUrl(clusterIds) {
  const params = {
    blockage: true,
    clusterIds,
  };
  return axios.post(`${API_URL}/instances/block`, params);
}

export function unblockMultiClusterFromUrl(clusterIds) {
  const params = {
    blockage: false,
    clusterIds,
  };
  return axios.post(`${API_URL}/instances/block`, params);
}

export function disconnectCluster(name) {
  return axios.put(`${API_URL}/instances/${name}/disconnect`);
}

export function disconnectMultiCluster(clusterIds) {
  return axios.post(`${API_URL}/instances/disconnect`, { clusterIds });
}

export function connectCluster(name) {
  return axios.put(`${API_URL}/instances/${name}/connect`);
}

export function switchClusterTraffic(name) {
  return axios.put(`${API_URL}/instances/${name}/switch-traffic`);
}

export function connectMultiCluster(clusterIds) {
  return axios.post(`${API_URL}/instances/connect`, { clusterIds });
}

export function upgradeToPremiumPackageBulk(clusterIds) {
  return axios.post(`${API_URL}/instances/premium`, { clusterIds });
}

export function removePremiumPackageBulk(clusterIds) {
  return axios.post(`${API_URL}/instances/remove-premium`, { clusterIds });
}

export function archiveCluster(name) {
  return axios.post(`${API_URL}/archives/archive`, {
    archiveRecord: { instanceName: name },
  });
}

export function transferClustersOwnership(cluster, clusterIds) {
  const params = {
    ...cluster,
    clusterIds,
  };
  return axios.post(`${API_URL}/instances/transfer`, params);
}

export function cloneCluster(name, cluster) {
  return axios.post(`${API_URL}/instances/${name}/clone`, cluster);
}

export function migrateCluster(name, migrationData) {
  return axios.post(`${API_URL}/instances/${name}/migrate`, migrationData);
}

export function getSizes() {
  return axios.get(`${API_URL}/sizes`);
}

export function getTimezones() {
  return axios.get(`${API_URL}/timezones`);
}

export function upgradeCluster(name, cluster) {
  return axios.put(`${API_URL}/instances/${name}/upgrade`, cluster);
}

export function upgradeJavaVersion(name: string, version: string) {
  return axios.put(`${API_URL}/instances/${name}/upgradejavaversion`, {
    version,
  });
}

export function upgradePythonVersion(
  name: string,
  requiredPythonVersion: string,
) {
  return axios.put(`${API_URL}/instances/${name}/upgradepythonversion`, {
    requiredPythonVersion,
  });
}

export function triggerCompatibilityUpgradeWorkflow({
  name,
  requiredVersions,
  incortaVersion,
}: {
  name: string;
  requiredVersions: Record<string, string | number>;
  incortaVersion?: string;
}) {
  return axios.post(
    `${API_URL}/instances/${name}/automated-compatibility-upgrade`,
    {
      requiredVersions,
      incortaVersion,
    },
  );
}

export function upgradeDisconnnectedCluster(name, cluster) {
  return axios.post(`${API_URL}/instances/${name}/pending`, {
    kind: 'UPGRADE_VERSION',
    ...cluster,
  });
}

export function upgradeMultiCluster(cluster, clusterIds) {
  const params = {
    ...cluster,
    clusterIds,
  };
  return axios.post(`${API_URL}/instances/upgrade`, params);
}

export function collectLogs(name, podType) {
  return axios.post(`${API_URL}/instances/${name}/collectLogs`, {
    podType: podType,
  });
}

export function sendLogsToCloudProvider(name, podType) {
  return axios.post(`${API_URL}/instances/${name}/sendLogsToCloudProvider`, {
    podType: podType,
  });
}

export function refreshScripts(name) {
  return axios.post(`${API_URL}/instances/${name}/refreshScripts`);
}

export function refreshScriptsMultiCluster(clusterIds) {
  const params = {
    clusterIds,
  };
  return axios.post(`${API_URL}/instances/refreshScripts`, params);
}

export function collectMetrics(name) {
  return axios.post(`${API_URL}/instances/${name}/collectMetrics`);
}

export function collectMetricsMultiCluster(clusterIds) {
  const params = {
    clusterIds,
  };
  return axios.post(`${API_URL}/instances/collectMetrics`, params);
}

export function toggleFeatureBits(
  clusterIDs,
  enabledFeatureBitsIDs,
  disabledFeatureBitsIDs,
) {
  const params = {
    clusterIDs,
    enabledFeatureBitsIDs,
    disabledFeatureBitsIDs,
  };
  return axios.put(`${API_URL}/instances/togglefeaturebits`, params);
}
export function sensitiveData(name) {
  return axios.get(`${API_URL}/instances/${name}/secrets`);
}

export function updateClusterImage(name) {
  return axios.put(`${API_URL}/instances/${name}/updateclusterimage`);
}

export function updateProbeCheck(name) {
  return axios.put(`${API_URL}/instances/${name}/updateprobecheck`);
}

export function updateExternalizedCacheCheck(name) {
  return axios.put(`${API_URL}/instances/${name}/updateExternalizedCacheCheck`);
}

export function updateOverwhelmedCheck(name) {
  return axios.put(`${API_URL}/instances/${name}/updateOverwhelmedCheck`);
}

export function getInstanceCustomization(name) {
  return axios.get(`${API_URL}/instances/${name}/customization`);
}

export function customizeInstance(name, customization) {
  return axios.put(`${API_URL}/instances/${name}/customization`, customization);
}

export function cleanupResources(filters) {
  return axios.post(`${API_URL}/instances/cleanup`, filters);
}

export function cleanupLogs() {
  return axios.get(`${API_URL}/instances/cleanup/logs`);
}

export function updateClusterSlaCost(id, cluster) {
  return axios.put(`${API_URL}/instances/${id}/updateSlaCost`, cluster);
}

export function updateMaintenanceMode(id, cluster) {
  return axios.put(`${API_URL}/instances/${id}/updateMaintenanceMode`, cluster);
}

export function getInstanceUsers(name) {
  return axios.get(`${API_URL}/instances/${name}/listusers`);
}

export function addUserRole(
  instanceName,
  roleName,
  userEmail,
  bypassValidationFlag,
) {
  return axios.put(`${API_URL}/instances/${instanceName}/adduserrole`, {
    bypassValidationFlag,
    roleName,
    userEmail,
  });
}

export function updateUserRole(instanceName, roleName, userEmail) {
  return axios.put(`${API_URL}/instances/${instanceName}/updateuserrole`, {
    roleName,
    userEmail,
  });
}

export function removeUserRole(instanceName, userEmail) {
  return axios.put(`${API_URL}/instances/${instanceName}/removeuserrole`, {
    userEmail,
  });
}

export function updateInstanceBlockedActions(blockedActions, clusterIds) {
  const params = {
    blockedActions,
    clusterIds,
  };
  return axios.post(
    `${API_URL}/instances/updateinstanceblockedactions`,
    params,
  );
}

export function removeInstanceLock(unlockedAction, clusterIds) {
  const params = {
    unlockedAction,
    clusterIds,
  };
  return axios.post(`${API_URL}/instances/removeinstancelock`, params);
}

export function addInstanceSelfLock(lockedAction, clusterIds) {
  const params = {
    lockedAction,
    clusterIds,
  };
  return axios.post(`${API_URL}/instances/addinstanceselflock`, params);
}

export function removeInstanceSelfLock(unlockedAction, clusterIds) {
  const params = {
    unlockedAction,
    clusterIds,
  };
  return axios.post(`${API_URL}/instances/removeinstanceselflock`, params);
}

export function getCluster(name) {
  return axios.get(`${API_URL}/instances/${name}`);
}

export function updateAutoScalingConfig({
  instanceID,
  instanceName,
  requestDelay,
  coolDelay,
  rejectedQueriesUp,
  rejectedQueriesDown,
  consecutiveQueries,
}: {
  instanceID: string;
  instanceName: string;
  requestDelay: number;
  coolDelay: number;
  rejectedQueriesUp: number;
  rejectedQueriesDown: number;
  consecutiveQueries: number;
}) {
  const data = {
    instanceID,
    requestDelay,
    coolDelay,
    rejectedQueriesUp,
    rejectedQueriesDown,
    consecutiveQueries,
  };
  return axios.put(
    `${API_URL}/instances/${instanceName}/updateautoscaleconfigs`,
    data,
  );
}

export async function syncPythonPackages({
  instanceID,
}: {
  instanceID: string;
}) {
  return axios.post(`${API_URL}/instances/syncpythonpackages`, { instanceID });
}

export function cloneClusterFromBackup(backupID, nameOfInstance) {
  return axios.post(`${API_URL}/instances/cloneclusterfrombackup`, {
    backupID,
    nameOfInstance,
  });
}

export function getRestoredClusters({
  page = 1,
  search,
  userid = null,
  limit = 10,
}) {
  return axios.get(`${API_URL}/instances/restored`, {
    params: { page, search, userid, limit },
  });
}

export function restoreClusterFromBackup(backupID, nameOfInstance) {
  return axios.post(`${API_URL}/instances/restoreclusterfrombackup`, {
    backupID,
    nameOfInstance,
  });
}
export function increaseDiskSize(diskUpsizeRecord, instanceID) {
  let body: any;
  if (diskUpsizeRecord.instanceID) {
    body = { ...diskUpsizeRecord };
  } else {
    body = {
      diskSize:
        diskUpsizeRecord.diskSize ||
        diskUpsizeRecord.dsize ||
        diskUpsizeRecord.dsizeLoader ||
        diskUpsizeRecord.dsizeCmc,
      instanceID: instanceID || diskUpsizeRecord.instanceID,
      replica: diskUpsizeRecord.replica,
    };
    if (diskUpsizeRecord.subclusterName) {
      body.subclusterName = diskUpsizeRecord.subclusterName;
    } else if (diskUpsizeRecord.service) {
      body.service = diskUpsizeRecord.service;
    }
  }
  return axios.put(`${API_URL}/instances/increasedisksize`, body);
}

export function customizeSelfManaged(id, customization) {
  return axios.post(
    `${API_URL}/instances/${id}/self-managed/customization`,
    customization,
  );
}

//for scaling services:
export function scaleCluster(name: string, body: ClusterScaleBody) {
  return axios.put(`${API_URL}/instances/${name}/scale`, body);
}

export async function getServiceNodes(
  instanceId: string,
  serviceStatus: boolean,
): Promise<{ data: ServicesStatusResponse }> {
  return axios.get(`${API_URL}/instances/${instanceId}/nodes`, {
    params: { serviceStatus },
  });
}

export function createSubCluster({
  clusterName,
  data,
}: {
  userId: string;
  clusterName: string;
  data: SubClusterCreateData;
}) {
  return axios.post<{ message: string }>(
    `${API_URL}/instances/${clusterName}/subcluster`,
    data,
  );
}

export function updateSubCluster({
  clusterName,
  data,
}: {
  userId: string;
  clusterName: string;
  data: SubClusterUpdateData;
}) {
  return axios.put<SelfManagedClusterUpdateData>(
    `${API_URL}/instances/${clusterName}/updatesubcluster`,
    data,
  );
}

export function connectSubCluster(name: string, body: { subCluster: string }) {
  return axios.put(`${API_URL}/instances/${name}/connectsubcluster`, body);
}
export function disconnectSubCluster(
  name: string,
  body: { subCluster: string },
) {
  return axios.put(`${API_URL}/instances/${name}/disconnectsubcluster`, body);
}
export function deleteSubCluster(clusterName: string, subClusterName: string) {
  return axios.delete(
    `${API_URL}/instances/${clusterName}/subcluster/${subClusterName}`,
  );
}

export function generatePassword(instaceID: string) {
  return axios.get(`${API_URL}/instances/${instaceID}/generatePassword`);
}

export function changePassword(
  instanceId: string,
  body: { password: string; isCloudAdmin: boolean },
) {
  return axios.post(`${API_URL}/instances/${instanceId}/changepassword`, body);
}
export function autoSuspendHistory(instanceId: string) {
  return axios.get(`${API_URL}/instances/${instanceId}/getautosuspendevents`);
}

export function getMaximumUsage({
  userId,
  instanceName,
}: {
  userId: string;
  instanceName: string;
}) {
  return axios.get<MaxIpuInfo>(`${API_URL}/users/${userId}/maxIpu`, {
    params: { instanceName },
  });
}
export function getInstanceDetails(name: string) {
  return axios.get<ClusterDetailsData>(
    `${API_URL}/instances/${name}/instancedetails`,
  );
}

export function getClusterExecutions(name: string) {
  return axios.get(`${API_URL}/instances/${name}/executions`);
}

export function getUpgradeProgress(name: string) {
  return axios.get(`${API_URL}/instances/${name}/upgrade/progress`);
}

export function getUpgradeHistory(name: string) {
  return axios.get<UpgradeHistoryResponse>(
    `${API_URL}/instances/${name}/upgrade/history`,
  );
}

export function getConsumptionArchiveFiles(id: string) {
  return axios.get(`${API_URL}/instances/${id}/consumption-archive`);
}

export function downloadConsumptionArchiveFile(id: string, file: string) {
  return axios.get(`${API_URL}/instances/${id}/consumption-archive/download`, {
    params: { file },
    responseType: 'blob',
  });
}

// IP Whitelisting
export function getInstanceWhitelists(name: string) {
  return axios.get(`${API_URL}/instances/${name}/whitelists`);
}

export function addInstanceWhitelist(
  name: string,
  whitelist: { range: string; description?: string },
) {
  return axios.post(`${API_URL}/instances/${name}/whitelists`, whitelist);
}

export function updateInstanceWhitelist(
  name: string,
  whitelistId: number,
  body: { range?: string; description?: string; active?: boolean },
) {
  return axios.put(
    `${API_URL}/instances/${name}/whitelists/${whitelistId}`,
    body,
  );
}

export function deleteInstanceWhitelist(name: string, whitelistId: number) {
  return axios.delete(`${API_URL}/instances/${name}/whitelists/${whitelistId}`);
}

export interface ClusterHealthInfo {
  status: number;
  uptime: number | null;
  cpuUsage: number | null;
  memoryUsage: Record<string, number> | null;
}

export function getClusterHealth(id: string) {
  return axios.get<ClusterHealthInfo>(`${API_URL}/instances/${id}/health`);
}

export interface TimeseriesPoint {
  time: string;
  value: number;
}

/** Pods are addressed by name, standalone deployments by their Deployment name. */
export type ServiceFilterKey = 'k8s.pod.name' | 'k8s.label.app' | 'k8s.deployment.name';

export interface ServiceMetricsParams {
  startTime: string;
  endTime: string;
  metric: 'cpuUsage' | 'memUsage' | 'memUtil';
  serviceName: string;
  /** Defaults to `k8s.pod.name` server-side. */
  filterKey?: ServiceFilterKey;
  context: 'incorta' | 'spark';
}
export interface ServiceMetricsResponse {
  timeseries: TimeseriesPoint[];
}

export function getServiceMetrics(
  id: string,
  params: ServiceMetricsParams,
): Promise<{ data: ServiceMetricsResponse }> {
  return axios.get(`${API_URL}/instances/${id}/metrics`, { params });
}

export interface AppServiceSizingResponse {
  service: string;
  memoryRequest: number | null;
  memoryLimit: number | null;
}

export function getAppServiceSizing(
  instanceId: string,
  service: string,
): Promise<{ data: AppServiceSizingResponse }> {
  return axios.get(
    `${API_URL}/instances/${instanceId}/app-services/${service}/sizing`,
  );
}

export async function getClusterServicesConsumption(
  userId: string,
  instanceName: string,
): Promise<AxiosResponse<{ nodes: ClusterServiceConsumption[] }>> {
  return axios.get(`${API_URL}/instances/${instanceName}/consumption/nodes`, {
    params: { userId },
  });
}
export interface DashboardBreakdownRecord {
  [field: string]: string | number | null;
}

export interface DashboardBreakdownResponse {
  status?: number;
  data: DashboardBreakdownRecord[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

export interface DashboardBreakdownParams {
  startTime: string;
  endTime: string;
  page?: number;
  pageSize?: number;
  command?: string;
  /** parameter KEY (e.g. "parameter1"), not the display name */
  parameter?: string;
}

export function getDashboardCommands(
  id: string,
  params: { startTime: string; endTime: string },
) {
  return axios.get<{ status?: number; commands: string[] }>(
    `${API_URL}/instances/${id}/dashboards/filters`,
    { params },
  );
}

export function getDashboardParams(id: string) {
  return axios.get<Record<string, unknown>>(
    `${API_URL}/instances/${id}/dashboards/parameters`,
  );
}

export function getDashboardBreakdown(
  id: string,
  params: DashboardBreakdownParams,
) {
  return axios.get<DashboardBreakdownResponse>(
    `${API_URL}/instances/${id}/dashboards/breakdown`,
    { params },
  );
}

export interface OsMemoryPoint {
  Time: string;
  Value: number;
}

export interface OsMemoryNode {
  NodeName: string;
  [seriesKey: string]: string | OsMemoryPoint[] | undefined;
}

export interface OsMemoryResponse {
  status?: number;
  data?: { body?: OsMemoryNode[] };
}

export interface OsMemoryParams {
  startTime: string;
  endTime: string;
}

export function getOsMemory(id: string, params: OsMemoryParams) {
  return axios.get<OsMemoryResponse>(`${API_URL}/instances/${id}/osmemory`, {
    params,
  });
}

export interface HeapMemoryResponse {
  status?: number;
  data?: {
    onHeap?: OsMemoryNode[];
    offHeap?: OsMemoryNode[];
  };
}

export type HeapMemoryParams = OsMemoryParams;

export function getHeapMemory(id: string, params: HeapMemoryParams) {
  return axios.get<HeapMemoryResponse>(
    `${API_URL}/instances/${id}/heapmemory`,
    { params },
  );
}

// ── Analytics historical logins (Arcane). One series per node. ──
export interface HistoricalLoginsPoint {
  Time: string;
  Value: number;
}

export interface HistoricalLoginsSeries {
  NodeName: string;
  Values: HistoricalLoginsPoint[];
}

export interface HistoricalLoginsResponse {
  status?: number;
  data?: { body?: HistoricalLoginsSeries[] };
}

export interface HistoricalLoginsParams {
  startTime: string;
  endTime: string;
}

export function getHistoricalLogins(
  id: string,
  params: HistoricalLoginsParams,
) {
  return axios.get<HistoricalLoginsResponse>(
    `${API_URL}/instances/${id}/historicallogins`,
    { params },
  );
}

// ── Load-jobs rendering (Arcane). Each schema carries the time ranges it ran in. ──
export interface LoadJobTimeRange {
  start_time: string;
  end_time: string;
}

export interface LoadJobSchemaRecord {
  schema: string;
  timerange: LoadJobTimeRange[];
}

export interface LoadJobsResponse {
  status?: number;
  loadJobs?: { body?: LoadJobSchemaRecord[] };
}

export interface LoadJobsParams {
  startTime: string;
  endTime: string;
  schemas?: string[];
}

export function getLoadJobs(id: string, params: LoadJobsParams) {
  return axios.get<LoadJobsResponse>(`${API_URL}/instances/${id}/loadjobs`, {
    params,
    // Send repeated `schemas=a&schemas=b` so the backend receives a real array.
    paramsSerializer: { indexes: null },
  });
}

// ── Paginated list of a cluster's schemas (Arcane). ──
export interface SchemasParams {
  startTime: string;
  endTime: string;
  page?: number;
  limit?: number;
}

export interface SchemasResponse {
  status?: number;
  body?: string[];
}

export function getClusterSchemas(id: string, params: SchemasParams) {
  return axios.get<SchemasResponse>(`${API_URL}/instances/${id}/schemas`, {
    params,
  });
}

export interface InstanceBackup {
  id: number;
  instanceID: string;
  backup: string;
  date: string;
  endDate: string | null;
  retention: number;
  frequency: number;
  parentID: number | null;
  fullBackup: boolean;
  backupType: string;
  backupStatus: string;
  createdAt: string;
  updatedAt: string;
}

export function getInstanceBackups(name: string) {
  return axios.get<{ backups: InstanceBackup[] }>(
    `${API_URL}/instances/${name}/instance-backups`,
  );
}

export function getSparkImagesSizes(name: string) {
  return axios.get<SparkImagesSizesData>(
    `${API_URL}/instances/${name}/spark-images-sizes`,
  );
}

export interface AuditUser {
  email: string;
  firstName: string;
  lastName: string;
}

export interface AuditRecord {
  id: number;
  action: string;
  platform: string;
  extraInfo: string | null;
  createdAt: string;
  user: AuditUser | null;
}

export interface AuditHistoryResponse {
  total: number;
  page: number;
  data: AuditRecord[];
}

export function getInstanceAuditHistory(name: string, page = 1, limit = 10) {
  return axios.get<AuditHistoryResponse>(
    `${API_URL}/instances/${name}/audit-history`,
    { params: { page, limit } },
  );
}

export type ClusterServiceNode = {
  id: number;
  type: 'loader' | 'analytics' | 'cmc' | 'subcluster';
  diskSize: number;
  availabeSize: number;
  replica: number;
  instanceID: string;
  subcluster: string | null;
  running: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
};

export function getInstanceConsumptionNodes(name: string) {
  return axios.get<{ nodes: ClusterServiceNode[] }>(
    `${API_URL}/instances/${name}/consumption-nodes`,
  );
}

export function getAuditArchiveFiles(id: string) {
  return axios.get(`${API_URL}/instances/${id}/audit-archive`);
}

export function downloadAuditArchiveFile(id: string, file: string) {
  return axios.get(`${API_URL}/instances/${id}/audit-archive/download`, {
    params: { file },
    responseType: 'blob',
  });
}
